import { WorkspaceApi } from '../apis/workspace-api';
import type { IPatentResponse, ILiteratureResponse, type IWSTaskListParams, type IWSTaskListResponse } from '../apis/workspace-api';
import { I18nService } from '@/services/i18n-service';
import { Message } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { DetailApi } from '../apis/detail-api';

export interface WorkspaceService {
    getWorkspacePatents(spaceId: string, folderId: string, page: number, limit: number, sort?: string): Promise<IPatentResponse>;
    getWorkspaceData(spaceId: string, folderId: string, page: number, limit: number, sort?: string, dataType?: string): Promise<ILiteratureResponse>;
    importFolder(projectId: string, fromSpace: string, fromFolder: string): Promise<void>;
    deleteWorkspacePatents(spaceId: string, folderId: string, patentIds: string[]): Promise<void>;
    deleteWorkspaceData(folderId: string, docIds: string[], dataType: string): Promise<void>;
    getTaskList(params: IWSTaskListParams): Promise<IWSTaskListResponse>;
    getRunningTaskCount(): Promise<number>;
}

export const WorkspaceService = Symbol('WorkspaceService');

@injectable()
export class WorkspaceServiceImpl implements WorkspaceService {
    constructor(
        @inject(WorkspaceApi) private readonly _workspaceApi: WorkspaceApi,
        @inject(DetailApi) private readonly _detailApi: DetailApi,
        @inject(Message) private readonly _message: Message,
        @inject(I18nService) private readonly _i18nService: I18nService
    ) {
    }

    getWorkspacePatents(spaceId: string, folderId: string, page: number, limit: number, sort?: string): Promise<IPatentResponse> {
        return this._workspaceApi.getWorkspacePatents(spaceId, folderId, page, limit, sort).then((patents) => {
            return patents;
        });
    }

    getWorkspaceData(spaceId: string, folderId: string, page: number, limit: number, sort?: string, dataType?: string): Promise<ILiteratureResponse> {
        return this._workspaceApi.getWorkspaceData(spaceId, folderId, page, limit, sort, dataType).then((data) => {
            return data;
        });
    }

    async importFolder(projectId: string, fromSpace: string, fromFolder: string): Promise<void> {
        if (!projectId || !fromSpace || !fromFolder) {
            throw new Error('projectId, space_id and folder_id are required');
        }

        try {
            await this._detailApi.importWorkspace(projectId, {
                space_id: fromSpace,
                folder_id: fromFolder,
            });

            this._message.success({
                message: this._i18nService.t('workProject.importWorkspaceSuccess'),
            });
        } catch (error) {
            this._message.error({
                message: this._i18nService.t('workProject.importWorkspaceFailed'),
            });
            throw error;
        }
    }

    deleteWorkspacePatents(spaceId: string, folderId: string, patentIds: string[]): Promise<void> {
        return this._workspaceApi.deleteWorkspacePatents(spaceId, folderId, patentIds).then(() => {
            this._message.success({
                message: this._i18nService.t('workProject.removeSuccess'),
            });
        });
    }

    deleteWorkspaceData(folderId: string, docIds: string[], dataType: string = 'PAPER'): Promise<void> {
        return this._workspaceApi.deleteWorkspaceData(folderId, docIds, dataType).then(() => {
            this._message.success({
                message: this._i18nService.t('workProject.removeSuccess'),
            });
        });
    }

    getTaskList(params: IWSTaskListParams): Promise<IWSTaskListResponse> {
        return this._workspaceApi.getTaskList(params);
    }

    async getRunningTaskCount(): Promise<number> {
        try {
            const result = await this._workspaceApi.getTaskList({
                type: 'ALL_TYPE',
                page: 1,
                limit: 50,
                status: 'PROGRESSING_STATUS',
            });
            return result.total;
        } catch (error) {
            return 0;
        }
    }
}
